import React, { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { RefreshCw, LucideIcon } from 'lucide-react'

interface PageProps {
  title: string
  subtitle?: string
  icon?: LucideIcon
  onRefresh?: () => void
  refreshing?: boolean
  children: ReactNode
  className?: string
}

const Page: React.FC<PageProps> = ({
  title,
  subtitle,
  icon: Icon,
  onRefresh,
  refreshing = false,
  children,
  className = ''
}) => {
  return (
    <div 
      className={`w-full h-full overflow-auto transition-colors duration-300 ${className}`}
      style={{ backgroundColor: 'var(--bg-primary)' }}
    >
      {/* Background Pattern */}
      <div 
        className="fixed inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      ></div>

      <div className="relative z-10 w-full h-full">
        {/* Page Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="border-b transition-colors duration-300"
          style={{ 
            backgroundColor: 'var(--bg-card)', 
            borderColor: 'var(--border-primary)' 
          }}
        >
          <div className="px-8 py-6">
            <div className="flex items-center justify-center relative">
              {/* Botão de refresh posicionado à direita */}
              {onRefresh && (
                <div className="absolute right-0">
                  <button 
                    onClick={onRefresh}
                    disabled={refreshing}
                    className="p-3 rounded-xl transition-all duration-300 hover:scale-105"
                    style={{ 
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              )}
              
              {/* Título centralizado */}
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <h1 
                    className="text-4xl font-bold mb-2 flex items-center justify-center gap-3"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {Icon && <Icon className="w-10 h-10" style={{ color: '#163965' }} />}
                    {title}
                  </h1>
                  {subtitle && (
                    <p 
                      className="text-lg"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Page Content */}
        <div className="p-8 space-y-8 min-h-full">
          {children}
        </div>
      </div>
    </div>
  )
}

export default Page
